/**
 * EstadisticasAdmin - Panel de Estadísticas Operativas
 * 
 * Explota los datos de jornadas_reales para:
 * 1. Overview: KPIs, gráficos de distribución y evolución
 * 2. Consulta rápida: qué hacía un conductor un día concreto
 * 3. Desviaciones: real vs calendario teórico
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    BarChart3, Search, GitCompare, Users, Calendar,
    TrendingUp, Clock, Bus, ArrowRight, ChevronDown,
    AlertTriangle, CheckCircle2, XCircle, Filter,
    RefreshCw, Download, Eye, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import api from '../services/api';

// Register Chart.js components
ChartJS.register(
    CategoryScale, LinearScale, BarElement, ArcElement,
    PointElement, LineElement, Title, Tooltip, Legend, Filler
);

// Use shared API client so auth works consistently in dev/prod
const apiFetch = async (endpoint) => api.get(endpoint);

// ─── Tab: Overview ───
const OverviewTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiFetch('/estadisticas/overview')
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <LoadingSpinner />;
    if (!data) return <ErrorMessage message="No se pudieron cargar las estadísticas" />;

    const { stats, tiposDist, lineasDist, turnosDist, dailyEvolution, topConductores } = data;

    // Doughnut chart colors
    const tipoColors = {
        'trabajo': '#22d3ee',
        'descanso': '#a78bfa',
        'no_disponible': '#f87171',
        'bloque_ajuste': '#fbbf24',
        'supervision': '#34d399',
        'vacaciones': '#60a5fa',
        'comp_anos_ant': '#fb923c',
        'revision_conduccion': '#e879f9',
        'revision': '#2dd4bf',
        'libre_convenio': '#a3e635',
        'anexo_libranza': '#f472b6'
    };

    const doughnutData = {
        labels: tiposDist.map(t => t.label),
        datasets: [{
            data: tiposDist.map(t => t.cantidad),
            backgroundColor: tiposDist.map(t => tipoColors[t.tipo] || '#6b7280'),
            borderColor: 'rgba(15, 17, 23, 0.8)',
            borderWidth: 2,
            hoverOffset: 8
        }]
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    color: '#94a3b8',
                    font: { size: 11 },
                    padding: 12,
                    usePointStyle: true,
                    pointStyleWidth: 10
                }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderColor: 'rgba(34, 211, 238, 0.2)',
                borderWidth: 1,
                titleColor: '#e2e8f0',
                bodyColor: '#94a3b8',
                padding: 12,
                callbacks: {
                    label: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const pct = ((ctx.raw / total) * 100).toFixed(1);
                        return ` ${ctx.label}: ${ctx.raw.toLocaleString()} (${pct}%)`;
                    }
                }
            }
        },
        cutout: '65%'
    };

    // Daily evolution chart
    const evolutionData = {
        labels: dailyEvolution.map(d => {
            const date = new Date(d.fecha + 'T12:00:00');
            return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        }),
        datasets: [
            {
                label: 'Trabajando',
                data: dailyEvolution.map(d => d.trabajando),
                backgroundColor: 'rgba(34, 211, 238, 0.6)',
                borderColor: '#22d3ee',
                borderWidth: 1
            },
            {
                label: 'Quincena',
                data: dailyEvolution.map(d => d.quincena),
                backgroundColor: 'rgba(251, 191, 36, 0.6)',
                borderColor: '#fbbf24',
                borderWidth: 1
            },
            {
                label: 'Descanso',
                data: dailyEvolution.map(d => d.descanso),
                backgroundColor: 'rgba(167, 139, 250, 0.6)',
                borderColor: '#a78bfa',
                borderWidth: 1
            },
            {
                label: 'No Disponible',
                data: dailyEvolution.map(d => d.no_disponible),
                backgroundColor: 'rgba(248, 113, 113, 0.5)',
                borderColor: '#f87171',
                borderWidth: 1
            },
            {
                label: 'Vacaciones',
                data: dailyEvolution.map(d => d.vacaciones),
                backgroundColor: 'rgba(96, 165, 250, 0.6)',
                borderColor: '#60a5fa',
                borderWidth: 1
            }
        ]
    };

    const evolutionOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: { color: '#94a3b8', usePointStyle: true, pointStyleWidth: 10, font: { size: 11 } }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderColor: 'rgba(34, 211, 238, 0.2)',
                borderWidth: 1,
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: { color: 'rgba(255,255,255,0.03)' },
                ticks: { color: '#64748b', font: { size: 10 }, maxRotation: 45 }
            },
            y: {
                stacked: true,
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#64748b' }
            }
        }
    };

    return (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <KPICard
                    icon={<BarChart3 className="w-5 h-5" />}
                    label="Total Registros"
                    value={stats.total.toLocaleString()}
                    color="cyan"
                />
                <KPICard
                    icon={<Users className="w-5 h-5" />}
                    label="Conductores"
                    value={stats.conductores}
                    color="green"
                />
                <KPICard
                    icon={<Calendar className="w-5 h-5" />}
                    label="Días Cubiertos"
                    value={stats.dias_cubiertos}
                    color="purple"
                />
                <KPICard
                    icon={<Clock className="w-5 h-5" />}
                    label="Desde"
                    value={stats.desde ? new Date(stats.desde + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '-'}
                    color="amber"
                />
                <KPICard
                    icon={<TrendingUp className="w-5 h-5" />}
                    label="Hasta"
                    value={stats.hasta ? new Date(stats.hasta + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '-'}
                    color="blue"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Donut Chart */}
                <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                    <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                        Distribución por Tipo
                    </h3>
                    <div style={{ height: '320px' }}>
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                </div>

                {/* Turno + Línea Distribution */}
                <div className="space-y-6">
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                        <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                            Distribución por Turno
                        </h3>
                        <div className="space-y-3">
                            {turnosDist.map(t => {
                                const pct = ((t.cantidad / stats.total) * 100).toFixed(1);
                                const colors = { 'M': 'bg-amber-400', 'T': 'bg-indigo-400', 'Sin turno': 'bg-gray-500' };
                                return (
                                    <div key={t.turno}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-300">{t.turno === 'M' ? '☀️ Mañana' : t.turno === 'T' ? '🌙 Tarde' : '⬜ Sin turno'}</span>
                                            <span className="text-gray-400">{t.cantidad.toLocaleString()} ({pct}%)</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                                            <div className={`h-full rounded-full ${colors[t.turno] || 'bg-gray-500'} transition-all duration-500`}
                                                style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                        <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-400"></div>
                            Distribución por Línea
                        </h3>
                        <div className="space-y-3">
                            {lineasDist.map(l => {
                                const pct = ((l.cantidad / stats.total) * 100).toFixed(1);
                                const colors = { 'BEI-A': 'bg-cyan-400', 'BEI-B': 'bg-emerald-400', 'Sin línea': 'bg-gray-500' };
                                return (
                                    <div key={l.linea}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-300">{l.linea === 'Sin línea' ? '⬜' : '🚌'} {l.linea}</span>
                                            <span className="text-gray-400">{l.cantidad.toLocaleString()} ({pct}%)</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                                            <div className={`h-full rounded-full ${colors[l.linea] || 'bg-blue-400'} transition-all duration-500`}
                                                style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Daily Evolution */}
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                    Evolución Diaria
                </h3>
                <div style={{ height: '300px' }}>
                    <Bar data={evolutionData} options={evolutionOptions} />
                </div>
            </div>

            {/* Top Conductores Table */}
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-pink-400"></div>
                    Ranking de Conductores
                </h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/5">
                                <th className="text-left py-3 px-3 text-gray-400 font-medium">#</th>
                                <th className="text-left py-3 px-3 text-gray-400 font-medium">Conductor</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">Total</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">🟢 Trabajo</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">🟡 Quincena</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">🟣 Descanso</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">🔵 Vacaciones</th>
                                <th className="text-center py-3 px-3 text-gray-400 font-medium">🔴 No Disp.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topConductores.map((c, i) => (
                                <tr key={c.id} className="border-b border-white/[0.02] hover:bg-white/[0.02] transition-colors">
                                    <td className="py-2.5 px-3 text-gray-500 font-mono text-xs">{i + 1}</td>
                                    <td className="py-2.5 px-3 text-gray-200 font-medium">{c.nombre}</td>
                                    <td className="py-2.5 px-3 text-center text-gray-300">{c.total_registros}</td>
                                    <td className="py-2.5 px-3 text-center text-cyan-400 font-medium">{c.dias_trabajo}</td>
                                    <td className="py-2.5 px-3 text-center text-amber-400">{c.dias_quincena}</td>
                                    <td className="py-2.5 px-3 text-center text-purple-400">{c.dias_descanso}</td>
                                    <td className="py-2.5 px-3 text-center text-blue-400">{c.dias_vacaciones}</td>
                                    <td className="py-2.5 px-3 text-center text-red-400">{c.dias_no_disponible}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ─── Tab: Consulta Rápida ───
const LookupTab = () => {
    const [fecha, setFecha] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [conductorId, setConductorId] = useState('');
    const [orderMode, setOrderMode] = useState('pdf'); // 'pdf' | 'alpha' | 'insert'
    const [conductores, setConductores] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        apiFetch('/estadisticas/conductores-list').then(setConductores).catch(console.error);
    }, []);

    const buscar = useCallback(async () => {
        setLoading(true);
        try {
            let url = `/estadisticas/lookup?fecha=${fecha}&order=${orderMode}`;
            if (conductorId) url += `&conductor_id=${conductorId}`;
            const data = await apiFetch(url);
            setResults(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [fecha, conductorId, orderMode]);

    // Navigate days
    const changeDay = (delta) => {
        const d = new Date(fecha + 'T12:00:00');
        d.setDate(d.getDate() + delta);
        setFecha(d.toISOString().split('T')[0]);
    };

    // Auto-search on date or conductor change
    useEffect(() => {
        if (fecha) buscar();
    }, [fecha, conductorId, orderMode]);

    const tipoColorMap = {
        'trabajo': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
        'supervision': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        'revision': 'bg-teal-500/20 text-teal-300 border-teal-500/30',
        'revision_conduccion': 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
        'bloque_ajuste': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        'asuntos_propios': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
        'descanso': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
        'vacaciones': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        'anexo_libranza': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
        'fiesta_compensada': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
        'comp_anos_ant': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
        'libre_convenio': 'bg-lime-500/20 text-lime-300 border-lime-500/30',
        'no_disponible': 'bg-red-500/20 text-red-300 border-red-500/30'
    };

    const formatDate = (d) => {
        const date = new Date(d + 'T12:00:00');
        return date.toLocaleDateString('es-ES', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    return (
        <div className="space-y-6">
            {/* Search Bar */}
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                    <Search className="w-4 h-4 text-cyan-400" />
                    Consulta Rápida — ¿Qué hacía un conductor un día concreto?
                </h3>
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex items-center gap-2">
                        <button onClick={() => changeDay(-1)}
                            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Fecha</label>
                            <input
                                type="date"
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-gray-200 focus:border-cyan-500/50 focus:outline-none transition-colors"
                            />
                        </div>
                        <button onClick={() => changeDay(1)}
                            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Conductor (opcional)</label>
                        <select
                            value={conductorId}
                            onChange={e => setConductorId(e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-gray-200 focus:border-cyan-500/50 focus:outline-none transition-colors min-w-[200px]"
                        >
                            <option value="" className="bg-gray-900">— Todos —</option>
                            {conductores.map(c => (
                                <option key={c.id} value={c.id} className="bg-gray-900">{c.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Orden visual</label>
                        <select
                            value={orderMode}
                            onChange={e => setOrderMode(e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-gray-200 focus:border-cyan-500/50 focus:outline-none transition-colors min-w-[220px]"
                        >
                            <option value="pdf" className="bg-gray-900">Orden PDF (revisión)</option>
                            <option value="alpha" className="bg-gray-900">Alfabético</option>
                            <option value="insert" className="bg-gray-900">Orden de importación</option>
                        </select>
                    </div>
                </div>
                {fecha && (
                    <p className="mt-3 text-sm text-gray-500 capitalize">{formatDate(fecha)}</p>
                )}
            </div>

            {/* Results */}
            {loading ? (
                <LoadingSpinner />
            ) : results ? (
                <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-300">
                            {results.total} registro{results.total !== 1 ? 's' : ''} encontrado{results.total !== 1 ? 's' : ''}
                        </h3>
                        <p className="text-xs text-gray-500">
                            Orden: {results.orderMode === 'alpha' ? 'Alfabético' : results.orderMode === 'insert' ? 'Importación' : 'PDF'}
                        </p>
                    </div>

                    {results.registros.length > 0 ? (
                        <div className="grid gap-3">
                            {results.registros.map((r, i) => (
                                <div key={i}
                                    className="flex items-center gap-4 p-4 bg-white/[0.02] rounded-xl border border-white/[0.04] hover:border-white/10 transition-colors">
                                    {/* Avatar */}
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 flex items-center justify-center text-sm font-bold text-cyan-300 shrink-0">
                                        {r.conductor_nombre?.charAt(0)}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-gray-200 font-medium">{r.conductor_nombre}</p>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <span className={`inline-flex px-2.5 py-0.5 rounded-lg text-xs font-medium border ${tipoColorMap[r.tipo] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'}`}>
                                                {r.tipo_label}
                                            </span>
                                            {r.linea && (
                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Bus className="w-3 h-3" /> {r.linea}
                                                </span>
                                            )}
                                            {r.servidor && (
                                                <span className="text-xs text-gray-500">Srv: {r.servidor}</span>
                                            )}
                                            {r.turno && (
                                                <span className="text-xs text-gray-500">
                                                    {r.turno === 'M' ? '☀️' : '🌙'} {r.turno === 'M' ? 'Mañana' : 'Tarde'}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Horario */}
                                    {r.horario && (
                                        <div className="text-right shrink-0">
                                            <p className="text-xs text-gray-500">Horario</p>
                                            <p className="text-sm text-gray-300 font-mono">{r.horario}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No hay registros para esta fecha</p>
                            <p className="text-xs mt-1">Prueba con otra fecha o conductor</p>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

// ─── Tab: Desviaciones ───
const DesviacionesTab = () => {
    const [conductores, setConductores] = useState([]);
    const [conductorId, setConductorId] = useState('');
    const [mode, setMode] = useState('individual'); // 'individual' | 'global'
    const [data, setData] = useState(null);
    const [globalData, setGlobalData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        apiFetch('/estadisticas/conductores-list').then(setConductores).catch(console.error);
    }, []);

    // Load individual deviations
    useEffect(() => {
        if (mode === 'individual' && conductorId) {
            setLoading(true);
            apiFetch(`/estadisticas/desviaciones?conductor_id=${conductorId}&year=2026`)
                .then(setData)
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [conductorId, mode]);

    // Load global deviations
    const loadGlobal = useCallback(() => {
        setLoading(true);
        apiFetch('/estadisticas/desviaciones-global?year=2026')
            .then(setGlobalData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (mode === 'global' && !globalData) loadGlobal();
    }, [mode]);

    const categoryColors = {
        'trabajo': { bg: 'bg-cyan-500/15', text: 'text-cyan-300', border: 'border-cyan-500/25' },
        'libranza': { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/25' },
        'vacaciones': { bg: 'bg-blue-500/15', text: 'text-blue-300', border: 'border-blue-500/25' },
        'no_disponible': { bg: 'bg-red-500/15', text: 'text-red-300', border: 'border-red-500/25' }
    };

    const getCategoryStyle = (cat) => categoryColors[cat] || { bg: 'bg-gray-500/15', text: 'text-gray-300', border: 'border-gray-500/25' };

    return (
        <div className="space-y-6">
            {/* Controls */}
            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                    <GitCompare className="w-4 h-4 text-amber-400" />
                    Desviaciones — Real vs Calendario Teórico
                </h3>

                <div className="flex flex-wrap items-end gap-4">
                    {/* Mode Toggle */}
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Vista</label>
                        <div className="flex bg-white/5 rounded-xl p-1">
                            <button
                                onClick={() => setMode('individual')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'individual' ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-500 hover:text-gray-300'
                                    }`}
                            >
                                Individual
                            </button>
                            <button
                                onClick={() => setMode('global')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'global' ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-500 hover:text-gray-300'
                                    }`}
                            >
                                Global (Todos)
                            </button>
                        </div>
                    </div>

                    {mode === 'individual' && (
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Conductor</label>
                            <select
                                value={conductorId}
                                onChange={e => setConductorId(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-gray-200 focus:border-cyan-500/50 focus:outline-none transition-colors min-w-[250px]"
                            >
                                <option value="" className="bg-gray-900">— Seleccionar conductor —</option>
                                {conductores.map(c => (
                                    <option key={c.id} value={c.id} className="bg-gray-900">{c.nombre}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {mode === 'global' && globalData && (
                        <button onClick={loadGlobal}
                            className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
                            <RefreshCw className="w-4 h-4" /> Refrescar
                        </button>
                    )}
                </div>
            </div>

            {loading && <LoadingSpinner />}

            {/* Individual Results */}
            {!loading && mode === 'individual' && data && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KPICard icon={<BarChart3 className="w-5 h-5" />} label="Total Registros"
                            value={data.totalRegistros} color="cyan" />
                        <KPICard icon={<CheckCircle2 className="w-5 h-5" />} label="Coinciden"
                            value={data.totalCoincidencias} color="green" />
                        <KPICard icon={<XCircle className="w-5 h-5" />} label="Desviaciones"
                            value={data.totalDesviaciones} color="red" />
                        <KPICard icon={<TrendingUp className="w-5 h-5" />} label="% Coincidencia"
                            value={`${data.porcentajeCoincidencia}%`} color="amber" />
                    </div>

                    {/* Concordance Bar */}
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-400">
                                Concordancia para <span className="text-gray-200 font-medium">{data.conductor?.nombre}</span>
                            </span>
                            <span className="text-gray-500">{data.porcentajeCoincidencia}%</span>
                        </div>
                        <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-700 ${data.porcentajeCoincidencia >= 80 ? 'bg-gradient-to-r from-emerald-500 to-green-400' :
                                    data.porcentajeCoincidencia >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' :
                                        'bg-gradient-to-r from-red-500 to-orange-400'
                                    }`}
                                style={{ width: `${data.porcentajeCoincidencia}%` }}
                            />
                        </div>
                    </div>

                    {/* Deviations List */}
                    {data.desviaciones.length > 0 && (
                        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                            <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                                Desviaciones detectadas ({data.desviaciones.length})
                            </h3>
                            <div className="space-y-2">
                                {data.desviaciones.map((d, i) => {
                                    const realStyle = getCategoryStyle(d.real.categoria);
                                    const teorStyle = getCategoryStyle(d.teorico.categoria);
                                    const dateObj = new Date(d.fecha + 'T12:00:00');
                                    const dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'short' });
                                    const dayNum = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

                                    return (
                                        <div key={i}
                                            className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/[0.03] hover:border-amber-500/20 transition-colors">
                                            {/* Date */}
                                            <div className="shrink-0 text-center w-16">
                                                <p className="text-[10px] uppercase text-gray-500">{dayName}</p>
                                                <p className="text-sm text-gray-300 font-medium">{dayNum}</p>
                                            </div>

                                            {/* Theoretical */}
                                            <span className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium border ${teorStyle.bg} ${teorStyle.text} ${teorStyle.border}`}>
                                                {d.teorico.label}
                                            </span>

                                            {/* Arrow */}
                                            <ArrowRight className="w-4 h-4 text-amber-400 shrink-0" />

                                            {/* Real */}
                                            <span className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium border ${realStyle.bg} ${realStyle.text} ${realStyle.border}`}>
                                                {d.real.tipo_label}
                                            </span>

                                            {/* Extra info */}
                                            <div className="flex-1 flex items-center gap-2 text-xs text-gray-500 overflow-hidden">
                                                {d.real.linea && <span className="flex items-center gap-1"><Bus className="w-3 h-3" />{d.real.linea}</span>}
                                                {d.real.horario && <span className="font-mono">{d.real.horario}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {data.desviaciones.length === 0 && data.totalRegistros > 0 && (
                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-8 text-center">
                            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                            <p className="text-emerald-300 font-medium">¡Concordancia perfecta!</p>
                            <p className="text-sm text-gray-500 mt-1">Todas las jornadas reales coinciden con el calendario teórico</p>
                        </div>
                    )}
                </>
            )}

            {/* Global Results */}
            {!loading && mode === 'global' && globalData && (
                <>
                    {/* Global Summary */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KPICard icon={<Users className="w-5 h-5" />} label="Conductores"
                            value={globalData.totalConductores} color="cyan" />
                        <KPICard icon={<BarChart3 className="w-5 h-5" />} label="Total Registros"
                            value={globalData.resumen.totalRegistros.toLocaleString()} color="blue" />
                        <KPICard icon={<CheckCircle2 className="w-5 h-5" />} label="Coincidencias"
                            value={globalData.resumen.totalCoincidencias.toLocaleString()} color="green" />
                        <KPICard icon={<XCircle className="w-5 h-5" />} label="Desviaciones"
                            value={globalData.resumen.totalDesviaciones.toLocaleString()} color="red" />
                    </div>

                    {/* Conductor Table */}
                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                        <h3 className="text-sm font-semibold text-gray-300 mb-4">
                            Concordancia por Conductor
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/5">
                                        <th className="text-left py-3 px-3 text-gray-400 font-medium">Conductor</th>
                                        <th className="text-center py-3 px-3 text-gray-400 font-medium">Registros</th>
                                        <th className="text-center py-3 px-3 text-gray-400 font-medium">✅ Coinciden</th>
                                        <th className="text-center py-3 px-3 text-gray-400 font-medium">⚠️ Desvían</th>
                                        <th className="text-left py-3 px-3 text-gray-400 font-medium w-48">Concordancia</th>
                                        <th className="text-center py-3 px-3 text-gray-400 font-medium">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {globalData.conductores.map(c => (
                                        <tr key={c.id} className="border-b border-white/[0.02] hover:bg-white/[0.02] transition-colors">
                                            <td className="py-2.5 px-3 text-gray-200 font-medium">{c.nombre}</td>
                                            <td className="py-2.5 px-3 text-center text-gray-400">{c.totalRegistros}</td>
                                            <td className="py-2.5 px-3 text-center text-emerald-400">{c.coincidencias}</td>
                                            <td className="py-2.5 px-3 text-center text-amber-400">{c.desviaciones}</td>
                                            <td className="py-2.5 px-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${c.porcentaje >= 80 ? 'bg-emerald-400' :
                                                                c.porcentaje >= 50 ? 'bg-amber-400' : 'bg-red-400'
                                                                }`}
                                                            style={{ width: `${c.porcentaje}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-400 w-10 text-right">{c.porcentaje}%</span>
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <button
                                                    onClick={() => { setMode('individual'); setConductorId(String(c.id)); }}
                                                    className="p-1.5 rounded-lg bg-white/5 hover:bg-cyan-500/20 text-gray-500 hover:text-cyan-300 transition-colors"
                                                    title="Ver detalle"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Empty states */}
            {!loading && mode === 'individual' && !conductorId && (
                <div className="text-center py-16 text-gray-500">
                    <GitCompare className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>Selecciona un conductor para ver sus desviaciones</p>
                </div>
            )}
        </div>
    );
};

// ─── Shared Components ───
const KPICard = ({ icon, label, value, color }) => {
    const colorMap = {
        cyan: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/20 text-cyan-400',
        green: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
        purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/20 text-purple-400',
        amber: 'from-amber-500/20 to-amber-500/5 border-amber-500/20 text-amber-400',
        blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/20 text-blue-400',
        red: 'from-red-500/20 to-red-500/5 border-red-500/20 text-red-400',
        pink: 'from-pink-500/20 to-pink-500/5 border-pink-500/20 text-pink-400'
    };
    const classes = colorMap[color] || colorMap.cyan;

    return (
        <div className={`bg-gradient-to-br ${classes} border rounded-2xl p-5`}>
            <div className="flex items-center gap-2 mb-2 opacity-70">
                {icon}
                <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-100">{value}</p>
        </div>
    );
};

const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
);

const ErrorMessage = ({ message }) => (
    <div className="text-center py-12 text-red-400">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
        <p>{message}</p>
    </div>
);

// ─── Main Component ───
const EstadisticasAdmin = () => {
    const [activeTab, setActiveTab] = useState('overview');

    const tabs = [
        { id: 'overview', label: 'Resumen', icon: <BarChart3 className="w-4 h-4" /> },
        { id: 'lookup', label: 'Consulta Rápida', icon: <Search className="w-4 h-4" /> },
        { id: 'desviaciones', label: 'Desviaciones', icon: <GitCompare className="w-4 h-4" /> }
    ];

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-3">
                        <span className="text-3xl">📊</span>
                        Estadísticas Operativas
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Datos reales extraídos de los PDFs de nombramiento
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white/[0.03] p-1.5 rounded-2xl border border-white/5 mb-6 w-fit">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${activeTab === tab.id
                            ? 'bg-cyan-500/15 text-cyan-300 shadow-lg shadow-cyan-500/5'
                            : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'lookup' && <LookupTab />}
            {activeTab === 'desviaciones' && <DesviacionesTab />}
        </div>
    );
};

export default EstadisticasAdmin;
